import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { drawdownAPI, fundAPI } from '../../services/api';
import ReceiptRemarksHint from '../../components/drawdowns/ReceiptRemarksHint';
import { ArrowLeft, Save, Plus, Trash2, Layers } from 'lucide-react';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'longtext', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'radio', label: 'Radio' },
];

const DrawdownForm = () => {
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Class-wise generation state
  const [classMode, setClassMode] = useState(false);
  const [fundClasses, setFundClasses] = useState([]); // master classes for selected fund
  const [classPercentages, setClassPercentages] = useState([]); // [{class_name, percentage_called}]
  const [classesLoading, setClassesLoading] = useState(false);
  const [form, setForm] = useState({
    fund_id: '',
    drawdown_name: '',
    percentage_called: '',
    notice_date: '',
    due_date: '',
    receipt_remarks_template: '{{drawdown_name}}',
    custom_fields: [],
  });

  useEffect(() => {
    fundAPI.getAll().then(res => { setFunds(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  // Load master classes whenever fund changes
  useEffect(() => {
    if (!form.fund_id) {
      setFundClasses([]);
      setClassPercentages([]);
      setClassMode(false);
      return;
    }
    setClassesLoading(true);
    fundAPI.getClasses(form.fund_id)
      .then(res => setFundClasses(res.data || []))
      .catch(() => setFundClasses([]))
      .finally(() => setClassesLoading(false));
    // Reset selected per-fund classes when fund changes
    setClassPercentages([]);
  }, [form.fund_id]);

  const toggleClassSelection = (className) => {
    setClassPercentages(prev => {
      const exists = prev.find(c => c.class_name === className);
      if (exists) return prev.filter(c => c.class_name !== className);
      return [...prev, { class_name: className, percentage_called: '' }];
    });
  };
  const updateClassPercentage = (className, value) => {
    setClassPercentages(prev =>
      prev.map(c => c.class_name === className ? { ...c, percentage_called: value } : c)
    );
  };

  const addCustomField = () => {
    setForm(prev => ({
      ...prev,
      custom_fields: [...prev.custom_fields, { field_name: '', field_type: 'text', field_value: null, options: [] }]
    }));
  };

  const updateCustomField = (idx, key, value) => {
    setForm(prev => {
      const fields = [...prev.custom_fields];
      fields[idx] = { ...fields[idx], [key]: value };
      return { ...prev, custom_fields: fields };
    });
  };

  const removeCustomField = (idx) => {
    setForm(prev => ({ ...prev, custom_fields: prev.custom_fields.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fund_id || !form.drawdown_name) return;
    // Validate class-wise mode
    if (classMode) {
      if (classPercentages.length === 0) {
        alert('Please select at least one class.');
        return;
      }
      const invalid = classPercentages.find(c => c.percentage_called === '' || isNaN(Number(c.percentage_called)));
      if (invalid) {
        alert(`Enter a valid percentage for class "${invalid.class_name}".`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        percentage_called: classMode ? 0 : (parseFloat(form.percentage_called) || 0),
        class_percentages: classMode
          ? classPercentages.map(c => ({ class_name: c.class_name, percentage_called: parseFloat(c.percentage_called) || 0 }))
          : null,
      };
      const res = await drawdownAPI.create(payload);
      navigate(`/drawdowns/${res.data.id}`);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create'); }
    finally { setSubmitting(false); }
  };

  const selectedFund = funds.find(f => f.id === form.fund_id);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="drawdown-form-page">
      <button onClick={() => navigate('/drawdowns')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={16} className="mr-1" /> Back to Drawdowns
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">Create New Drawdown</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Fund Selection */}
        <div className="bg-white border rounded-lg p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-800">Drawdown Summary</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Fund *</label>
              <select value={form.fund_id} onChange={e => setForm(p => ({ ...p, fund_id: e.target.value }))}
                required className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="dd-fund-select">
                <option value="">Select Fund</option>
                {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Drawdown Name *</label>
              <input type="text" value={form.drawdown_name} onChange={e => setForm(p => ({ ...p, drawdown_name: e.target.value }))}
                required placeholder="e.g. First Drawdown" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="dd-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">% of Capital Commitment Called {!classMode && '*'}</label>
              <input
                type="number"
                step="0.01"
                value={form.percentage_called}
                onChange={e => setForm(p => ({ ...p, percentage_called: e.target.value }))}
                placeholder={classMode ? 'Set per-class below' : 'e.g. 25'}
                disabled={classMode}
                className={`w-full border rounded-lg px-3 py-2 text-sm mt-1 ${classMode ? 'bg-gray-50 text-gray-400' : ''}`}
                data-testid="dd-pct-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Currency</label>
              <input type="text" readOnly value={selectedFund?.fund_currency || '-'} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 bg-gray-50 text-gray-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Notice Date</label>
              <input type="date" value={form.notice_date} onChange={e => setForm(p => ({ ...p, notice_date: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="dd-notice-date" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="dd-due-date" />
            </div>
          </div>

          {/* Class-wise generation toggle */}
          {form.fund_id && (
            <div className="border-t pt-4">
              <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="classmode-toggle-wrap">
                <input
                  type="checkbox"
                  checked={classMode}
                  onChange={e => setClassMode(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  data-testid="dd-classmode-toggle"
                />
                <span className="text-sm font-medium text-gray-800 inline-flex items-center gap-1.5">
                  <Layers size={14} className="text-gray-500" />
                  Generate Class-wise (different called % per class)
                </span>
              </label>

              {classMode && (
                <div className="mt-3 border rounded-lg p-3 bg-blue-50/30" data-testid="class-percentages-panel">
                  {classesLoading ? (
                    <p className="text-sm text-gray-500">Loading classes…</p>
                  ) : fundClasses.length === 0 ? (
                    <p className="text-sm text-amber-700">No fund classes defined. Add classes under the Fund first.</p>
                  ) : (
                    <>
                      <p className="text-xs text-gray-600 mb-2">Pick the classes you want to include and set each class's called percentage. Investors in non-selected classes will not receive a notice for this drawdown.</p>
                      <div className="space-y-2">
                        {fundClasses.map(fc => {
                          const sel = classPercentages.find(c => c.class_name === fc.name);
                          return (
                            <div key={fc.id || fc.name} className="flex items-center gap-3 bg-white rounded-md border px-3 py-2">
                              <label className="inline-flex items-center gap-2 flex-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!sel}
                                  onChange={() => toggleClassSelection(fc.name)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  data-testid={`class-checkbox-${fc.name}`}
                                />
                                <span className="text-sm font-medium text-gray-800">{fc.name}</span>
                                {fc.description && <span className="text-xs text-gray-400 ml-1">— {fc.description}</span>}
                              </label>
                              {sel && (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={sel.percentage_called}
                                    onChange={e => updateClassPercentage(fc.name, e.target.value)}
                                    placeholder="e.g. 30"
                                    className="w-24 border rounded px-2 py-1 text-sm text-right"
                                    data-testid={`class-pct-${fc.name}`}
                                  />
                                  <span className="text-xs text-gray-500">%</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {classPercentages.length > 0 && (
                        <p className="text-xs text-blue-700 mt-2">
                          {classPercentages.length} class{classPercentages.length !== 1 ? 'es' : ''} selected.
                          Drawdown will be calculated as <strong>class commitment × class percentage</strong> for each investor.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="border-t pt-4">
            <label className="text-sm font-medium text-gray-700">Receipt Remarks Template</label>
            <input type="text" value={form.receipt_remarks_template}
              onChange={e => setForm(p => ({ ...p, receipt_remarks_template: e.target.value }))}
              placeholder="{{drawdown_name}}"
              className="w-full border rounded-lg px-3 py-2 text-sm mt-1 font-mono"
              data-testid="dd-receipt-remarks-template" />
            <p className="text-xs text-gray-500 mt-1">
              Template used to auto-fill the <strong>Remarks</strong> field on every Drawdown Received transaction
              created from this drawdown's receipts. Use any of the placeholders below.
            </p>
            <ReceiptRemarksHint />
          </div>
        </div>

        {/* Custom Fields */}
        <div className="bg-white border rounded-lg p-5">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-800">Custom Fields</h2>
            <button type="button" onClick={addCustomField} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700">
              <Plus size={14} className="mr-1" /> Add Field
            </button>
          </div>
          {form.custom_fields.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No custom fields added. Click "Add Field" to define additional fields.</p>
          ) : (
            <div className="space-y-3">
              {form.custom_fields.map((cf, idx) => (
                <div key={idx} className="flex gap-3 items-start p-3 border rounded-lg bg-gray-50">
                  <div className="flex-1">
                    <input type="text" value={cf.field_name} onChange={e => updateCustomField(idx, 'field_name', e.target.value)}
                      placeholder="Field Name" className="w-full border rounded px-2 py-1.5 text-sm mb-2" />
                    <select value={cf.field_type} onChange={e => updateCustomField(idx, 'field_type', e.target.value)}
                      className="border rounded px-2 py-1.5 text-sm">
                      {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                    </select>
                    {(cf.field_type === 'dropdown' || cf.field_type === 'radio') && (
                      <input type="text" value={(cf.options || []).join(', ')}
                        onChange={e => updateCustomField(idx, 'options', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                        placeholder="Options (comma separated)" className="w-full border rounded px-2 py-1.5 text-sm mt-2" />
                    )}
                  </div>
                  <button type="button" onClick={() => removeCustomField(idx)} className="p-1 text-gray-400 hover:text-red-500 mt-1">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/drawdowns')} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
          <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="submit-drawdown-btn">
            <Save size={14} className="mr-1 inline" /> {submitting ? 'Creating...' : 'Create Drawdown'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DrawdownForm;
